import React, { useEffect, useMemo } from 'react'
import { batch } from '@preact/signals-react'
import { disabledBefore } from '@ui-seeker/core'
import { getNextDate } from '@ui-seeker/utils'

import {bookingRange, dateRange, sites, offices, officesPickoff, tiers, officeGroup, isOfficeGroup, isSiteGroup, siteGroup} from '@signals'
import { BACKEND_APIS, COMMON_FILTERS_COMPONENTS, DATE_PRESETS, DATE_PRESETS_AFTER, SERVICES_TYPE } from '@constants'
import { AppCommonFilters } from '@components'
import { useValidatorSeeker } from '@ui-seeker/app'
import { BIFormData } from 'Pages/BusinessLogistics/BI/Page/@signals'
import { BISchema } from 'Pages/BusinessLogistics/BI/Page/schema'
import { areCommonFiltersValid, commonFilterErrors } from '@signals/commonFilters/filters'
import BIFiltersFooterActions from 'Pages/BusinessLogistics/BI/Page/Filters/Footer'


export default function BIFilters() {

	const formDataForValidation = useMemo(() => ({
		...BIFormData.value,
		_isOfficeGroupToggle: isOfficeGroup.value
	}), [BIFormData.value, isOfficeGroup.value])

	const { errors, isValid } = useValidatorSeeker(formDataForValidation, BISchema)

	const onAjaxCommon = options => options.map(option => ({
		label: `${option.label}`,
		value: `${option.value}`,
	}))

	useEffect(() => {
		areCommonFiltersValid.value = isValid
		commonFilterErrors.value = errors
	}, [JSON.stringify(errors)])

	useEffect(() => {
		dateRange.value = [getNextDate({ days: 0 }), getNextDate({ days: 7 })]
	}, [])

	return <AppCommonFilters
		headerComponents={[
			{
				name: COMMON_FILTERS_COMPONENTS.DAILY_PRICE_TOGGLE,
				grid: { xs: 1.5 },
				error: errors?.isDailyPrice,
			}, {
                name: COMMON_FILTERS_COMPONENTS.IS_OFFICE_GROUP,
                grid: { xs: 1.5 },
            }, {
                name: COMMON_FILTERS_COMPONENTS.IS_SITE_GROUP,
                grid: { xs: 1.5 },
            }, {
				name: COMMON_FILTERS_COMPONENTS.IS_PREV_COMPARISON,
				grid: { xs: 5 },
			},
		]}
		components={[
			{
				name: COMMON_FILTERS_COMPONENTS.HOW_TO_GROUP,
				grid: { xs: 2 },
				disableClearable: true,
				filters: {
					service_type: SERVICES_TYPE.BI,
				},
				error: errors?.reportType,
			}, !isOfficeGroup.value && {
				name: COMMON_FILTERS_COMPONENTS.OFFICE_PICKUP,
				label: 'Pick up Office',
				multiple: true,
				grid: { xs: 3 },
				filters: {
					service_type: SERVICES_TYPE.BI,
					id_office_pickup: offices.value,
				},
				error: errors?.officePickupID,
			}, isOfficeGroup.value && {
                name: COMMON_FILTERS_COMPONENTS.OFFICE_GROUP,
                label: 'Pickup office Group',
                multiple: true,
                grid: { xs: 3 },
                filters: {
                    service_type: SERVICES_TYPE.BI,
                    officeGroupID: officeGroup.value,
                },
                error: errors?.officeGroupID,
            }, {
				name: COMMON_FILTERS_COMPONENTS.OFFICE_DROPOFF,
				label: 'Drop off Office',
				multiple: false,
				grid: { xs: 3 },
				filters: {
					service_type: SERVICES_TYPE.BI,
					id_office_dropoff: officesPickoff.value,
				},
				error: errors?.officesPickoff,
			}, !isSiteGroup.value && {
				name: COMMON_FILTERS_COMPONENTS.SITE,
				grid: { xs: 4 },
				urlAjax: `${BACKEND_APIS.MASTER_DATA}/options/site`,
				filters: {
					service_type: SERVICES_TYPE.BI,
				},
				error: errors?.siteID,
			}, isSiteGroup.value && {
                name: COMMON_FILTERS_COMPONENTS.SITE_GROUP,
                label: 'Site Group',
                multiple: true,
                grid: { xs: 4 },
                filters: {
                    service_type: SERVICES_TYPE.BI,
                    siteGroupID: siteGroup.value,
                },
                error: errors?.siteGroupID,
            },{
				name: COMMON_FILTERS_COMPONENTS.BOOKING_DATE,
				grid: { xs: 2 },
				value: bookingRange.value,
				onChange: e => batch(() => {
					bookingRange.value = e
					dateRange.value = [getNextDate({ days: 0 }, e[0]), getNextDate({ days: 0 }, e[1])]
				}),
				ranges: DATE_PRESETS,
				label: 'Booking date range',
				error: errors?.startBookingDate,
			}, {
				name: COMMON_FILTERS_COMPONENTS.DATE_RANGE,
				grid: { xs: 2 },
				ranges: DATE_PRESETS_AFTER,
				label: 'Pickup date range',
				shouldDisableDate: disabledBefore(bookingRange.value[0]),
				error: errors?.startPickupDate,
			}, {
				name: COMMON_FILTERS_COMPONENTS.GROUP_BY,
				grid: { xs: 2 },
				disableClearable: true,
				error: errors?.groupBy,
			}, {
				name: COMMON_FILTERS_COMPONENTS.CATEGORIES,
				urlAjax: `${BACKEND_APIS.MASTER_DATA}/options/category`,
				filters: {
					siteID: sites.value,
					service_type: SERVICES_TYPE.BI,
				},
				grid: { xs: 3 },
				error: errors?.categoryID,
			}, {
				name: COMMON_FILTERS_COMPONENTS.RENTAL_LENGTH,
				multiple: true,
				grid: { xs: 1 },
				urlAjax: `${BACKEND_APIS.MASTER_DATA}/options/lor`,
				onAjax: onAjaxCommon,
				filters: {
					service_type: SERVICES_TYPE.BI,
				},
				error: errors?.lor,
			}, {
				name: COMMON_FILTERS_COMPONENTS.TIERS,
				grid: { xs: 2 },
				filters: {
					siteID: sites.value,
					selected: tiers.value,
					service_type: SERVICES_TYPE.BI,
				},
				error: errors?.tiers,
			}, {
				name: COMMON_FILTERS_COMPONENTS.PROVIDERS,
				urlAjax: `${BACKEND_APIS.MASTER_DATA}/options/provider`,
				filters: {
					siteID: sites.value,
					service_type: SERVICES_TYPE.BI,
				},
				error: errors?.providerID,
			}, {
				name: COMMON_FILTERS_COMPONENTS.CURRENCY,
				grid: { xs: 1 },
				urlAjax: `${BACKEND_APIS.MASTER_DATA}/options/currencies`,
				filters: {
					siteID: sites.value,
					service_type: SERVICES_TYPE.BI,
				},
				error: errors?.currency,
			}, {
				name: COMMON_FILTERS_COMPONENTS.PREV_COMPARISON,
				grid: { xs: 2 },
				error: errors?.isPreviousComparison,
			},
		]}
		featuresComponents={[
			{
				name: COMMON_FILTERS_COMPONENTS.ACRISS,
				urlAjax: `${BACKEND_APIS.MASTER_DATA}/options/acriss`,
				filters: {
					service_type: SERVICES_TYPE.BI,
				},
				error: errors?.acriss,
			}, {
				name: COMMON_FILTERS_COMPONENTS.MILEAGE,
				urlAjax: `${BACKEND_APIS.MASTER_DATA}/options/mileage`,
				filters: {
					service_type: SERVICES_TYPE.BI,
				},
				error: errors?.unlimited_km,
			}, {
				name: COMMON_FILTERS_COMPONENTS.TRANSMISSION,
				urlAjax: `${BACKEND_APIS.MASTER_DATA}/options/transmission`,
				filters: {
					service_type: SERVICES_TYPE.BI,
				},
				error: errors?.automatic,
			}, {
				name: COMMON_FILTERS_COMPONENTS.FUEL_POLICY,
				urlAjax: `${BACKEND_APIS.MASTER_DATA}/options/fuel`,
				filters: {
					service_type: SERVICES_TYPE.BI,
				},
				error: errors?.fuelPolicyID,
			}, {
				name: COMMON_FILTERS_COMPONENTS.INSURANCE,
				urlAjax: `${BACKEND_APIS.MASTER_DATA}/options/insurance`,
				filters: {
					service_type: SERVICES_TYPE.BI,
				},
				error: errors?.insurance,
			}, {
				name: COMMON_FILTERS_COMPONENTS.PREPAID,
				urlAjax: `${BACKEND_APIS.MASTER_DATA}/options/payment_method`,
				filters: {
					service_type: SERVICES_TYPE.BI,
				},
				error: errors?.is_prepaid,
			}, {
				name: COMMON_FILTERS_COMPONENTS.IN_TERMINAL,
				urlAjax: `${BACKEND_APIS.MASTER_DATA}/options/terminal`,
				filters: {
					service_type: SERVICES_TYPE.BI,
				},
				error: errors?.in_terminal_pickup,
			}, {
				name: COMMON_FILTERS_COMPONENTS.WEB_SUPPLIERS,
				urlAjax: `${BACKEND_APIS.MASTER_DATA}/options/web_suppliers`,
				filters: {
					service_type: SERVICES_TYPE.BI,
				},
				error: errors?.webSuppliers,
			},
		]}
		Footer={BIFiltersFooterActions}
	/>
}